import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { runWhenPageReady } from '../utils/PageLifecycle';

gsap.registerPlugin(ScrollTrigger);

/**
 * FooterMenuAnimation
 * 
 * Animates footer navigation menus with:
 * - All headings fade in + move up simultaneously (~300ms)
 * - Links "flutter" in with opacity-only, staggered in rows across columns
 * - Newsletter and Root & Stem logo animate with the sequence
 */
class FooterMenuAnimation {
  constructor() {
    this.menuColumns = document.querySelectorAll('.footer-menu-column');
    this.newsletter = document.querySelector('#footer-newsletter');
    this.rootStem = document.querySelector('#footer-root-stem');
    
    if (this.menuColumns.length === 0) {
      console.log('FooterMenuAnimation: No menu columns found');
      return;
    }
    
    this.init();
  }
  
  init() {
    // Collect all headings
    const headings = [];
    this.menuColumns.forEach(col => {
      const title = col.querySelector('.footer-menu-title');
      if (title) headings.push(title);
    });
    
    // Add newsletter title to headings group
    const newsletterTitle = this.newsletter?.querySelector('.newsletter-title');
    if (newsletterTitle) headings.push(newsletterTitle);
    
    // Collect all links organized by row
    const linksByRow = this.organizeLinksIntoRows();
    
    // Collect newsletter form and Root & Stem
    const newsletterForm = this.newsletter?.querySelector('.newsletter-form');
    const newsletterPrivacy = this.newsletter?.querySelector('.newsletter-privacy');
    
    // Set initial states - force GSAP control, override any CSS transitions
    gsap.set(headings, {
      opacity: 0,
      y: 30,
      clearProps: 'transition'
    });
    
    gsap.set(linksByRow.flat(), {
      opacity: 0,
      clearProps: 'transition'
    });
    
    if (newsletterForm) {
      gsap.set(newsletterForm, { 
        opacity: 0,
        clearProps: 'transition'
      });
    }
    if (newsletterPrivacy) {
      gsap.set(newsletterPrivacy, { 
        opacity: 0,
        clearProps: 'transition'
      });
    }
    if (this.rootStem) {
      gsap.set(this.rootStem, { 
        opacity: 0,
        clearProps: 'transition'
      });
    }
    
    // Create animation timeline triggered by scroll
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: '.site-footer',
        start: 'top 85%', // Trigger when footer is 85% down the viewport
        once: true // Only animate once
      }
    });
    
    // Phase 1: All headings simultaneously (with Y transform)
    // Start after 300ms delay
    tl.to(headings, {
      opacity: 1,
      y: 0,
      duration: 0.4,
      ease: 'power2.out',
      clearProps: 'transform',
      delay: 0.3 // Delay happens AFTER scroll trigger fires
    });
    
    // Phase 2: Links "flutter" in rows with opacity only
    // Add small delay after headings settle
    tl.add('linksStart', '+=0.1'); // 100ms pause
    
    linksByRow.forEach((row, index) => {
      tl.to(row, {
        opacity: 1,
        duration: 0.4,
        ease: 'power2.out'
      }, `linksStart+=${index * 0.05}`); // 50ms stagger between rows
    });
    
    // Phase 3: Newsletter form and Root & Stem
    if (newsletterForm) {
      tl.to(newsletterForm, {
        opacity: 1,
        duration: 0.4,
        ease: 'power2.out'
      }, `linksStart+=${linksByRow.length * 0.05}`);
    }
    
    if (newsletterPrivacy) {
      tl.to(newsletterPrivacy, {
        opacity: 1,
        duration: 0.4,
        ease: 'power2.out'
      }, `linksStart+=${(linksByRow.length + 1) * 0.05}`);
    }
    
    if (this.rootStem) {
      tl.to(this.rootStem, {
        opacity: 1,
        duration: 0.4,
        ease: 'power2.out'
      }, `linksStart+=${(linksByRow.length + 1) * 0.05}`);
    }
    
    console.log('FooterMenuAnimation: Initialized with', {
      headings: headings.length,
      rows: linksByRow.length,
      linksPerRow: linksByRow.map(r => r.length)
    });
  }
  
  /**
   * Organize links into rows for curtain/blinds effect
   * Returns array of arrays, where each sub-array is a "row" across all columns
   */
  organizeLinksIntoRows() {
    const columnLinks = [];
    
    // Get all links from each column
    this.menuColumns.forEach(col => {
      const links = Array.from(col.querySelectorAll('.footer-menu-list li'));
      columnLinks.push(links);
    });
    
    // Find the maximum number of links in any column
    const maxLinks = Math.max(...columnLinks.map(col => col.length));
    
    // Build rows: first link from each column, then second link, etc.
    const rows = [];
    for (let i = 0; i < maxLinks; i++) {
      const row = [];
      columnLinks.forEach(colLinks => {
        if (colLinks[i]) {
          row.push(colLinks[i]);
        }
      });
      if (row.length > 0) {
        rows.push(row);
      }
    }
    
    return rows;
  }
}

/**
 * Initialize footer menu animation on page load
 */
function initFooterMenuAnimation() {
  // Check if we're in a page transition context where footer might be excluded
  const footer = document.querySelector('.site-footer');
  if (!footer) {
    console.log('FooterMenuAnimation: Footer not found');
    return;
  }
  
  new FooterMenuAnimation();
}

runWhenPageReady(initFooterMenuAnimation);

export default FooterMenuAnimation;
